import {
  View,
  Text,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
} from "react-native";
import React, { useState, useEffect } from "react";
import CustomAlert from "../common/CustomAlert";
import Ionicons from "@expo/vector-icons/Ionicons";
import i18n from "../../locales/i18n";
import MaterialCommunityIcons from "@expo/vector-icons/MaterialCommunityIcons";
import { AntDesign } from "@expo/vector-icons";
import * as Print from "expo-print";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

// Type definitions
interface Product {
  product_name: string;
  quantity: number;
  price_sell: number;
}

interface BillInfo {
  id_facture?: string;
  facture: string;
  profit: number;
  total: number;
  capital: number;
  created_at: string;
  listofproducts: Product[];
}

interface BillProps {
  info: BillInfo;
  handleDelete?: ((id: string) => void) | null;
  buttonSpinnerDelete?: boolean;
  printed?: boolean;
  total?: string;
  created_at?: string;
}

const Bill: React.FC<BillProps> = ({
  info,
  handleDelete = null,
  buttonSpinnerDelete = false,
  printed = true,
  total = "",
  created_at = "",
}) => {
  const [showMore, setShowMore] = useState(false);
  const [formattedDate, setFormattedDate] = useState("");
  const [formattedTime, setFormattedTime] = useState("");
  const { user } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    // Parse the ISO date string
    const isoDate = info.created_at || created_at; // e.g., "2025-03-15T14:14:56.344Z"
    const date = new Date(isoDate);

    // Format the date (e.g., "March 15, 2025")
    const optionsDate: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    const formattedDate = date.toLocaleDateString("en-US", optionsDate);

    // Format the time (e.g., "02:14 PM")
    const optionsTime: Intl.DateTimeFormatOptions = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    const formattedTime = date.toLocaleTimeString("en-US", optionsTime);

    // Update state
    setFormattedDate(formattedDate);
    setFormattedTime(formattedTime);
  }, [info.created_at, created_at]);

  const handlePrint = async () => {
    const html = `
    <html>
      <head>
        <meta charset="utf-8" />
        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 16px;
            color: #333;
          }
          .card {
            border: 1px solid #279F9E;
            border-radius: 12px;
            padding: 16px;
            box-shadow: 0 4px 6px rgba(39, 159, 158, 0.3);
          }
          .header {
            display: flex;
            justify-content: space-between;
            margin-bottom: 12px;
          }
          .section {
            display: flex;
            justify-content: space-between;
            margin-top: 12px;
          }
          .title {
            font-size: 18px;
            font-weight: bold;
            color: #279F9E;
          }
          .value {
            font-size: 16px;
            font-weight: bold;
          }
          .product-table {
            margin-top: 20px;
            width: 100%;
            border-collapse: collapse;
          }
          .product-table th {
            background-color: #279F9E;
            color: white;
            padding: 12px 8px;
            text-align: center;
            font-weight: bold;
            border: 1px solid #279F9E;
          }
          .product-table td {
            padding: 10px 8px;
            text-align: center;
            border: 1px solid #ddd;
          }
          .product-table tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          .product-table tr:hover {
            background-color: #f5f5f5;
          }
          .quantity-cell {
            background-color: #E53935;
            color: white;
            font-weight: bold;
            border-radius: 4px;
          }
        </style>
      </head>
      <body>
        <div class="card">
          <div class="header">
            <span>${formattedDate}</span>
            <span>${formattedTime}</span>
            <span>${user.storename ? user.storename : "no name"}</span>
          </div>
          <div class="section">
            <div class="title">${i18n.t("total")}</div>
            <div class="value">${info.total} ${i18n.t("currency")}</div>
          </div>

          <table class="product-table">
            <thead>
              <tr>
                <th>${i18n.t("productName")}</th>
                <th>${i18n.t("quantity")}</th>
                <th>${i18n.t("price")}</th>
              </tr>
            </thead>
            <tbody>
              ${info.listofproducts
                .map(
                  (p: Product) => `
                <tr>
                  <td>${p.product_name}</td>
                  <td class="quantity-cell">${p.quantity}</td>
                  <td>${p.price_sell} ${i18n.t("currency")}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
        </div>
      </body>
    </html>
  `;

    try {
      await Print.printAsync({ html });
    } catch (err) {}
  };

  const [showDeleteAlert, setShowDeleteAlert] = useState(false);

  const confirmDelete = () => {
    setShowDeleteAlert(true);
  };

  return (
    <View className="bg-white rounded-2xl border-2 border-gray-100 shadow-md overflow-hidden mt-4">
      <CustomAlert
        visible={showDeleteAlert}
        title={i18n.t("confirmDelete") || "Confirm Delete"}
        message={
          i18n.t("deleteMessage") ||
          "Are you sure you want to delete this bill? This action cannot be undone."
        }
        icon="trash-outline"
        iconColor="#DC2626"
        onDismiss={() => setShowDeleteAlert(false)}
        buttons={[
          { text: i18n.t("cancel") || "Cancel", style: "cancel" },
          {
            text: i18n.t("delete") || "Delete",
            style: "destructive",
            onPress: () => {
              if (handleDelete && info.id_facture) {
                handleDelete(info.id_facture);
              }
            },
          },
        ]}
      />
      {handleDelete && (
        <TouchableOpacity
          onPress={confirmDelete}
          disabled={buttonSpinnerDelete}
          className="absolute right-3 top-3 z-50 bg-white rounded-full shadow-lg p-1"
        >
          <AntDesign name="close-circle" size={28} color="#EF4444" />
        </TouchableOpacity>
      )}

      {buttonSpinnerDelete && (
        <View className="absolute inset-0 bg-white/95 z-40 flex items-center justify-center">
          <ActivityIndicator size="large" color="#279F9E" />
          <Text className="text-center mt-4 text-gray-700 font-medium">
            {i18n.t("billLoadingDelete")}
          </Text>
        </View>
      )}

      {/* Header Section with Gradient Background */}
      <View className="bg-gradient-to-r from-[#279F9E] to-[#1d7a7a] px-5 py-4">
        <View className="flex-row items-center justify-between">
          <View className="flex-row items-center gap-2">
            <View className="bg-white/20 rounded-full p-2">
              <MaterialCommunityIcons name="receipt" size={20} color="white" />
            </View>
            <Text className="text-white text-xl font-bold">{info.facture}</Text>
          </View>
          <View className="items-end">
            <Text className="text-white/80 text-sm">{formattedDate}</Text>
            <Text className="text-white/80 text-sm mt-1">{formattedTime}</Text>
          </View>
        </View>
      </View>

      {/* Stats Section */}
      <View className="px-5 py-4">
        <View className="flex-row items-center justify-between gap-2">
          {/* Profit Card - Only show if profit exists */}
          {info.profit !== undefined && info.profit !== null && (
            <View className="flex-1 bg-green-50 rounded-xl p-3 border border-green-200">
              <View className="flex-row items-center justify-center mb-1">
                <MaterialCommunityIcons
                  name="cash-multiple"
                  size={18}
                  color="#10B981"
                />
                <Text className="text-xs text-green-700 font-semibold ml-1">
                  {i18n.t("profit")}
                </Text>
              </View>
              <Text className="text-center text-lg font-bold text-green-700">
                {info.profit}
              </Text>
              <Text className="text-center text-xs text-green-600">
                {i18n.t("currency")}
              </Text>
            </View>
          )}

          {/* Total Card */}
          <View className="flex-1 bg-blue-50 rounded-xl p-3 border border-blue-200">
            <View className="flex-row items-center justify-center mb-1">
              <MaterialCommunityIcons
                name="currency-usd"
                size={18}
                color="#3B82F6"
              />
              <Text className="text-xs text-blue-700 font-semibold ml-1">
                {i18n.t("total")}
              </Text>
            </View>
            <Text className="text-center text-lg font-bold text-blue-700">
              {total ? total : info.total}
            </Text>
            <Text className="text-center text-xs text-blue-600">
              {i18n.t("currency")}
            </Text>
          </View>

          {/* Sales Card */}
          <View className="flex-1 bg-purple-50 rounded-xl p-3 border border-purple-200">
            <View className="flex-row items-center justify-center mb-1">
              <MaterialCommunityIcons name="cart" size={18} color="#8B5CF6" />
              <Text className="text-xs text-purple-700 font-semibold ml-1">
                {i18n.t("sales")}
              </Text>
            </View>
            <Text className="text-center text-lg font-bold text-purple-700">
              {info.listofproducts.length}
            </Text>
            <Text className="text-center text-xs text-purple-600">
              {i18n.t("items")}
            </Text>
          </View>
        </View>
      </View>

      {!showMore && (
        <View className="px-5 pb-4">
          <TouchableOpacity
            onPress={() => {
              setShowMore(true);
            }}
            className="flex-row items-center justify-center bg-gray-100 rounded-lg py-3 px-4 gap-2"
          >
            <Ionicons name="eye" size={20} color="#279F9E" />
            <Text className="text-[#279F9E] font-semibold">
              {i18n.t("showmore")}
            </Text>
            <Ionicons name="chevron-down" size={16} color="#279F9E" />
          </TouchableOpacity>
        </View>
      )}

      {showMore && (
        <>
          <View className="px-5">
            {/* Products Header */}
            <View className="flex-row items-center mb-3">
              <MaterialCommunityIcons
                name="package-variant"
                size={20}
                color="#279F9E"
              />
              <Text className="text-lg font-bold text-gray-800 ml-2">
                {i18n.t("products")}
              </Text>
            </View>

            {/* Table Header */}
            <View
              style={{
                flexDirection: "row",
                backgroundColor: "#279F9E",
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                overflow: "hidden",
                borderWidth: 2,
                borderColor: "#279F9E",
              }}
            >
              <View
                style={{
                  flex: 2,
                  paddingVertical: 12,
                  paddingHorizontal: 8,
                  borderRightWidth: 1,
                  borderRightColor: "rgba(255, 255, 255, 0.3)",
                }}
              >
                <Text
                  style={{
                    color: "white",
                    fontWeight: "bold",
                    textAlign: "center",
                    fontSize: 13,
                  }}
                >
                  {i18n.t("productName")}
                </Text>
              </View>
              <View
                style={{
                  width: 70,
                  paddingVertical: 12,
                  paddingHorizontal: 4,
                  borderRightWidth: 1,
                  borderRightColor: "rgba(255, 255, 255, 0.3)",
                }}
              >
                <Text
                  style={{
                    color: "white",
                    fontWeight: "bold",
                    textAlign: "center",
                    fontSize: 13,
                  }}
                >
                  {i18n.t("quantity")}
                </Text>
              </View>
              <View
                style={{ flex: 1.5, paddingVertical: 12, paddingHorizontal: 8 }}
              >
                <Text
                  style={{
                    color: "white",
                    fontWeight: "bold",
                    textAlign: "center",
                    fontSize: 13,
                  }}
                >
                  {i18n.t("price")}
                </Text>
              </View>
            </View>

            {/* Table Body */}
            <View
              style={{
                borderWidth: 2,
                borderTopWidth: 0,
                borderColor: "#279F9E",
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
                overflow: "hidden",
              }}
            >
              {info.listofproducts.map((item, index) => (
                <View
                  key={index}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    backgroundColor: index % 2 === 0 ? "#F9FAFB" : "#FFFFFF",
                    borderBottomWidth:
                      index !== info.listofproducts.length - 1 ? 1 : 0,
                    borderBottomColor: "#E5E7EB",
                  }}
                >
                  <View
                    style={{
                      flex: 2,
                      paddingVertical: 12,
                      paddingHorizontal: 8,
                      borderRightWidth: 1,
                      borderRightColor: "#E5E7EB",
                    }}
                  >
                    <Text
                      style={{
                        color: "#1F2937",
                        fontWeight: "500",
                        fontSize: 13,
                        textAlign: "left",
                      }}
                    >
                      {item.product_name}
                    </Text>
                  </View>
                  <View
                    style={{
                      width: 70,
                      paddingVertical: 12,
                      paddingHorizontal: 4,
                      alignItems: "center",
                      justifyContent: "center",
                      borderRightWidth: 1,
                      borderRightColor: "#E5E7EB",
                    }}
                  >
                    <View
                      style={{
                        backgroundColor: "#FEF3C7",
                        borderWidth: 2,
                        borderColor: "#F59E0B",
                        borderRadius: 6,
                        paddingHorizontal: 8,
                        paddingVertical: 4,
                      }}
                    >
                      <Text
                        style={{
                          color: "#92400E",
                          fontWeight: "bold",
                          fontSize: 14,
                        }}
                      >
                        {item.quantity}
                      </Text>
                    </View>
                  </View>
                  <View
                    style={{
                      flex: 1.5,
                      paddingVertical: 12,
                      paddingHorizontal: 8,
                    }}
                  >
                    <Text
                      style={{
                        color: "#1F2937",
                        fontWeight: "bold",
                        textAlign: "center",
                        fontSize: 13,
                      }}
                    >
                      {item.price_sell}
                    </Text>
                    <Text
                      style={{
                        color: "#6B7280",
                        fontSize: 11,
                        textAlign: "center",
                      }}
                    >
                      {i18n.t("currency")}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </View>

          <View className="px-5 py-4">
            <TouchableOpacity
              onPress={() => {
                setShowMore(false);
              }}
              className="flex-row items-center justify-center bg-gray-100 rounded-lg py-3 px-4 gap-2"
            >
              <Ionicons name="eye-off" size={20} color="#6B7280" />
              <Text className="text-gray-600 font-semibold">
                {i18n.t("showless")}
              </Text>
              <Ionicons name="chevron-up" size={16} color="#6B7280" />
            </TouchableOpacity>
          </View>
        </>
      )}
      {/* Print Button */}
      {printed && (
        <View className="px-5 pb-4 pt-2 border-t border-gray-200">
          <TouchableOpacity
            onPress={handlePrint}
            className="flex-row items-center justify-center bg-[#279F9E] rounded-xl py-3 px-4 gap-2 shadow-md"
          >
            <MaterialCommunityIcons name="printer" size={22} color="white" />
            <Text className="text-white font-bold text-base">
              {i18n.t("print")}
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

export default Bill;
